// frontend/src/pages/admin/OrderDetail.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from '../../utils/axios';
import { toast } from 'react-toastify';
import {
  FiArrowLeft,
  FiUser,
  FiPhone,
  FiMail,
  FiMapPin,
  FiPackage,
  FiCreditCard,
  FiCheckCircle,
  FiXCircle,
  FiClock,
  FiFileText,
  FiAlertCircle,
  FiTruck,
  FiEdit,
  FiDollarSign
} from 'react-icons/fi';

const OrderDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  // Modals
  const [showVerifyModal, setShowVerifyModal] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showCreditPaymentModal, setShowCreditPaymentModal] = useState(false);

  // Form data
  const [verifyNote, setVerifyNote] = useState('');
  const [rejectNote, setRejectNote] = useState('');
  const [newStatus, setNewStatus] = useState('');
  const [statusNote, setStatusNote] = useState('');
  const [creditPaymentAmount, setCreditPaymentAmount] = useState('');
  const [creditPaymentNote, setCreditPaymentNote] = useState('');

  useEffect(() => {
    fetchOrderDetail();
  }, [id]);

  const fetchOrderDetail = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`/api/reports/orders/${id}`);

      if (response.data.success) {
        setOrder(response.data.data.order);
      }
    } catch (error) {
      console.error('Fetch order detail error:', error);
      toast.error('Захиалгын дэлгэрэнгүй татахад алдаа гарлаа');
      navigate('/admin/orders');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyPayment = async () => {
    if (!window.confirm('Төлбөрийг баталгаажуулах уу?')) return;

    setActionLoading(true);
    try {
      const response = await axios.put(`/api/reports/orders/${id}/verify-payment`, {
        admin_note: verifyNote
      });

      if (response.data.success) {
        toast.success('Төлбөр амжилттай баталгаажлаа');
        setShowVerifyModal(false);
        setVerifyNote('');
        fetchOrderDetail();
      }
    } catch (error) {
      console.error('Verify payment error:', error);
      toast.error(error.response?.data?.message || 'Алдаа гарлаа');
    } finally {
      setActionLoading(false);
    }
  };

  const handleRejectPayment = async () => {
    if (!rejectNote.trim()) {
      toast.warning('Татгалзах шалтгаан бичнэ үү');
      return;
    }

    if (!window.confirm('Төлбөрийг татгалзах уу?')) return;

    setActionLoading(true);
    try {
      const response = await axios.put(`/api/reports/orders/${id}/reject-payment`, {
        admin_note: rejectNote
      });

      if (response.data.success) {
        toast.success('Төлбөр татгалзсан');
        setShowRejectModal(false);
        setRejectNote('');
        fetchOrderDetail();
      }
    } catch (error) {
      console.error('Reject payment error:', error);
      toast.error(error.response?.data?.message || 'Алдаа гарлаа');
    } finally {
      setActionLoading(false);
    }
  };

  const handleUpdateStatus = async () => {
    if (!newStatus) {
      toast.warning('Статус сонгоно уу');
      return;
    }

    if (!window.confirm('Захиалгын статусыг өөрчлөх үү?')) return;

    setActionLoading(true);
    try {
      const response = await axios.put(`/api/reports/orders/${id}/status`, {
        status: newStatus,
        notes: statusNote
      });

      if (response.data.success) {
        toast.success('Статус шинэчлэгдлээ');
        setShowStatusModal(false);
        setNewStatus('');
        setStatusNote('');
        fetchOrderDetail();
      }
    } catch (error) {
      console.error('Update status error:', error);
      toast.error(error.response?.data?.message || 'Алдаа гарлаа');
    } finally {
      setActionLoading(false);
    }
  };

  // ✅ ШИНЭ - Зээлийн төлбөр бүртгэх
  const handleRecordCreditPayment = async () => {
    const amount = parseFloat(creditPaymentAmount);
    
    if (!amount || amount <= 0) {
      toast.warning('Төлбөрийн дүн оруулна уу');
      return;
    }

    const remainingAmount = order.total_amount - order.paid_amount;
    if (amount > remainingAmount) {
      toast.warning(`Төлбөрийн дүн үлдэгдэл (${remainingAmount.toLocaleString()}₮) -ээс их байна`);
      return;
    }

    if (!window.confirm(`${amount.toLocaleString()}₮ төлбөр бүртгэх үү?`)) return;

    setActionLoading(true);
    try {
      const response = await axios.post(`/api/reports/orders/${id}/credit-payment`, {
        amount: amount,
        note: creditPaymentNote
      });

      if (response.data.success) {
        toast.success('Зээлийн төлбөр амжилттай бүртгэгдлээ');
        setShowCreditPaymentModal(false);
        setCreditPaymentAmount('');
        setCreditPaymentNote('');
        fetchOrderDetail();
      }
    } catch (error) {
      console.error('Record credit payment error:', error);
      toast.error(error.response?.data?.message || 'Алдаа гарлаа');
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      pending: { bg: 'bg-yellow-100', text: 'text-yellow-800', icon: FiClock, label: 'Хүлээгдэж буй' },
      accepted: { bg: 'bg-blue-100', text: 'text-blue-800', icon: FiCheckCircle, label: 'Баталгаажсан' },
      prepared: { bg: 'bg-purple-100', text: 'text-purple-800', icon: FiPackage, label: 'Бэлтгэгдсэн' },
      dispatched: { bg: 'bg-indigo-100', text: 'text-indigo-800', icon: FiTruck, label: 'Хүргэлтэнд гарсан' },
      delivered: { bg: 'bg-green-100', text: 'text-green-800', icon: FiCheckCircle, label: 'Хүргэгдсэн' },
      cancelled: { bg: 'bg-red-100', text: 'text-red-800', icon: FiXCircle, label: 'Цуцлагдсан' }
    };

    const badge = badges[status] || badges.pending;
    const Icon = badge.icon;

    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-sm font-medium ${badge.bg} ${badge.text}`}>
        <Icon size={16} />
        {badge.label}
      </span>
    );
  };

  const getPaymentStatusBadge = (paymentStatus) => {
    const badges = {
      unpaid: { bg: 'bg-red-100', text: 'text-red-800', label: 'Төлөөгүй' },
      partial: { bg: 'bg-orange-100', text: 'text-orange-800', label: 'Хэсэгчлэн' },
      paid: { bg: 'bg-green-100', text: 'text-green-800', label: 'Төлсөн' },
      refunded: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Буцаасан' }
    };

    const badge = badges[paymentStatus] || badges.unpaid;

    return (
      <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium ${badge.bg} ${badge.text}`}>
        {badge.label}
      </span>
    );
  };

  const getTransactionStatusBadge = (status) => {
    const badges = {
      pending_verification: { bg: 'bg-yellow-100', text: 'text-yellow-800', label: 'Баталгаажуулалт хүлээгдэж байна' },
      verified: { bg: 'bg-green-100', text: 'text-green-800', label: 'Баталгаажсан' },
      rejected: { bg: 'bg-red-100', text: 'text-red-800', label: 'Татгалзсан' }
    };

    const badge = badges[status] || badges.pending_verification;

    return (
      <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium ${badge.bg} ${badge.text}`}>
        {badge.label}
      </span>
    );
  };

  if (loading || !order) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="spinner-lg mb-4"></div>
          <p className="text-gray-600">Уншиж байна...</p>
        </div>
      </div>
    );
  }

  // Calculate remaining amount for credit payment
  const remainingAmount = order.total_amount - order.paid_amount;
  const hasUnpaidCredit = order.payment_method === 'credit' && remainingAmount > 0;

  return (
    <div className="order-detail-page">
      {/* Header */}
      <div className="mb-6">
        <Link
          to="/admin/orders"
          className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 mb-4"
        >
          <FiArrowLeft />
          Буцах
        </Link>

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              Захиалга #{order.order_number}
            </h1>
            <p className="text-gray-600 mt-1">
              {new Date(order.created_at).toLocaleString('mn-MN')}
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            {getStatusBadge(order.status)}
            {getPaymentStatusBadge(order.payment_status)}
            
            <button
              onClick={() => setShowStatusModal(true)}
              className="btn btn-secondary btn-sm"
            >
              <FiEdit className="mr-1" />
              Статус өөрчлөх
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Order Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Order Items */}
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Захиалгын бараа</h3>
            </div>
            <div className="card-body p-0">
              <div className="overflow-x-auto max-w-full">
                <table className="table min-w-full">
                  <thead className="table-header">
                    <tr>
                      <th className="table-th">Бараа</th>
                      <th className="table-th text-right">Үнэ</th>
                      <th className="table-th text-center">Тоо ширхэг</th>
                      <th className="table-th text-right">Нийт</th>
                    </tr>
                  </thead>
                  <tbody>
                    {order.items.map((item, index) => (
                      <tr key={index} className="table-row">
                        <td className="table-td">
                          <div className="flex items-center gap-3">
                            {item.image_url ? (
                              <img
                                src={`${axios.defaults.baseURL}${item.image_url}`}
                                alt={item.product_name}
                                className="w-12 h-12 object-cover rounded"
                              />
                            ) : (
                              <div className="w-12 h-12 bg-gray-200 rounded flex items-center justify-center">
                                <FiPackage className="text-gray-400" />
                              </div>
                            )}
                            <div>
                              <p className="font-medium text-gray-900">{item.product_name}</p>
                              <p className="text-xs text-gray-500">{item.barcode}</p>
                            </div>
                          </div>
                        </td>
                        <td className="table-td text-right">
                          ₮{item.unit_price.toLocaleString()}
                        </td>
                        <td className="table-td text-center">
                          {item.quantity} {item.unit}
                        </td>
                        <td className="table-td text-right font-semibold">
                          ₮{item.subtotal.toLocaleString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          {/* Payment Transaction */}
          {order.transaction && (
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">Төлбөрийн мэдээлэл</h3>
              </div>
              <div className="card-body">
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Гүйлгээний ID</p>
                      <p className="font-mono text-sm font-semibold text-gray-900">
                        {order.transaction.transaction_id}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Статус</p>
                      {getTransactionStatusBadge(order.transaction.status)}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Банк</p>
                      <p className="font-semibold text-gray-900">
                        {order.transaction.bank_name}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Дансны дугаар</p>
                      <p className="font-mono text-sm font-semibold text-gray-900">
                        {order.transaction.account_number}
                      </p>
                    </div>
                  </div>

                  <div>
                    <p className="text-sm text-gray-600 mb-1">Эзэмшигчийн нэр</p>
                    <p className="font-semibold text-gray-900">
                      {order.transaction.account_name}
                    </p>
                  </div>

                  {order.transaction.customer_note && (
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Харилцагчийн тэмдэглэл</p>
                      <p className="text-sm text-gray-900 bg-gray-50 p-3 rounded">
                        {order.transaction.customer_note}
                      </p>
                    </div>
                  )}

                  {order.transaction.admin_note && (
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Админы тэмдэглэл</p>
                      <p className="text-sm text-gray-900 bg-gray-50 p-3 rounded">
                        {order.transaction.admin_note}
                      </p>
                    </div>
                  )}

                  {order.transaction.verified && (
                    <div className="pt-3 border-t">
                      <p className="text-sm text-gray-600">
                        Баталгаажуулсан: <span className="font-semibold">{order.transaction.verified_by_name}</span>
                      </p>
                      <p className="text-xs text-gray-500">
                        {new Date(order.transaction.verified_at).toLocaleString('mn-MN')}
                      </p>
                    </div>
                  )}

                  {/* Verification Actions */}
                  {order.transaction.status === 'pending_verification' && (
                    <div className="flex gap-3 pt-4 border-t">
                      <button
                        onClick={() => setShowVerifyModal(true)}
                        className="btn btn-success flex-1"
                      >
                        <FiCheckCircle className="mr-2" />
                        Баталгаажуулах
                      </button>
                      <button
                        onClick={() => setShowRejectModal(true)}
                        className="btn btn-danger flex-1"
                      >
                        <FiXCircle className="mr-2" />
                        Татгалзах
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* ✅ ШИНЭ - Credit Payment Section */}
          {hasUnpaidCredit && (
            <div className="card border-2 border-orange-200 bg-orange-50">
              <div className="card-header bg-orange-100">
                <h3 className="card-title text-orange-900 flex items-center gap-2">
                  <FiCreditCard />
                  Зээлийн төлбөр
                </h3>
              </div>
              <div className="card-body">
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Нийт дүн:</p>
                    <p className="text-xl font-bold text-gray-900">
                      ₮{order.total_amount.toLocaleString()}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-600 mb-1">Төлсөн:</p>
                    <p className="text-xl font-bold text-green-600">
                      ₮{order.paid_amount.toLocaleString()}
                    </p>
                  </div>
                </div>
                
                <div className="bg-white rounded-lg p-4 mb-4">
                  <p className="text-sm text-gray-600 mb-1">Үлдэгдэл зээл:</p>
                  <p className="text-2xl font-bold text-red-600">
                    ₮{remainingAmount.toLocaleString()}
                  </p>
                </div>

                <button
                  onClick={() => setShowCreditPaymentModal(true)}
                  className="btn btn-primary w-full"
                >
                  <FiDollarSign className="mr-2" />
                  Төлбөр бүртгэх
                </button>
              </div>
            </div>
          )}

          {/* Notes */}
          {order.notes && (
            <div className="card">
              <div className="card-header">
                <h3 className="card-title">Тэмдэглэл</h3>
              </div>
              <div className="card-body">
                <p className="text-gray-700">{order.notes}</p>
              </div>
            </div>
          )}
        </div>

        {/* Right Column - Summary & Customer Info */}
        <div className="space-y-6">
          {/* Customer Info */}
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Харилцагчийн мэдээлэл</h3>
            </div>
            <div className="card-body space-y-3">
              <div className="flex items-start gap-3">
                <FiUser className="text-gray-400 mt-1 flex-shrink-0" />
                <div>
                  <p className="text-sm text-gray-600">Нэр</p>
                  <p className="font-semibold text-gray-900">{order.customer_name}</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <FiPhone className="text-gray-400 mt-1 flex-shrink-0" />
                <div>
                  <p className="text-sm text-gray-600">Утас</p>
                  <p className="font-semibold text-gray-900">{order.customer_phone}</p>
                </div>
              </div>

              <div className="flex items-start gap-3">
                <FiMail className="text-gray-400 mt-1 flex-shrink-0" />
                <div>
                  <p className="text-sm text-gray-600">Имэйл</p>
                  <p className="font-semibold text-gray-900">{order.customer_email}</p>
                </div>
              </div>

              {order.delivery_address && (
                <div className="flex items-start gap-3">
                  <FiMapPin className="text-gray-400 mt-1 flex-shrink-0" />
                  <div>
                    <p className="text-sm text-gray-600">Хүргэлтийн хаяг</p>
                    <p className="font-semibold text-gray-900">{order.delivery_address}</p>
                  </div>
                </div>
              )}

              <div className="pt-3 border-t">
                <span className={`badge ${
                  order.customer_type === 'wholesale' ? 'badge-info' : 'badge-gray'
                }`}>
                  {order.customer_type === 'wholesale' ? 'Бөөний худалдаа' : 'Жижиглэн'}
                </span>
              </div>
            </div>
          </div>

          {/* Order Summary */}
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Захиалгын дүн</h3>
            </div>
            <div className="card-body space-y-3">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Дэд дүн:</span>
                <span className="font-semibold text-gray-900">
                  ₮{order.subtotal.toLocaleString()}
                </span>
              </div>

              {order.discount_rate > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">
                    Хөнгөлөлт ({order.discount_rate}%):
                  </span>
                  <span className="font-semibold text-green-600">
                    -₮{order.discount_amount.toLocaleString()}
                  </span>
                </div>
              )}

              {order.tax > 0 && (
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">НӨАТ (10%):</span>
                  <span className="font-semibold text-gray-900">
                    ₮{order.tax.toLocaleString()}
                  </span>
                </div>
              )}

              <div className="pt-3 border-t">
                <div className="flex justify-between">
                  <span className="font-semibold text-gray-900">Нийт дүн:</span>
                  <span className="text-xl font-bold text-blue-600">
                    ₮{order.total_amount.toLocaleString()}
                  </span>
                </div>
              </div>

              <div className="pt-3 border-t">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Төлсөн:</span>
                  <span className="font-semibold text-green-600">
                    ₮{order.paid_amount.toLocaleString()}
                  </span>
                </div>
                <div className="flex justify-between text-sm mt-1">
                  <span className="text-gray-600">Үлдэгдэл:</span>
                  <span className={`font-semibold ${remainingAmount > 0 ? 'text-red-600' : 'text-green-600'}`}>
                    ₮{remainingAmount.toLocaleString()}
                  </span>
                </div>
              </div>

              <div className="pt-3 border-t">
                <p className="text-xs text-gray-600 mb-1">Төлбөрийн хэлбэр</p>
                <span className={`badge ${
                  order.payment_method === 'credit' ? 'badge-orange' : 'badge-gray'
                }`}>
                  {order.payment_method === 'cash' && 'Бэлэн'}
                  {order.payment_method === 'card' && 'Карт'}
                  {order.payment_method === 'bank_transfer' && 'Шилжүүлэг'}
                  {order.payment_method === 'credit' && '⚠️ Зээл'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      {/* Verify Modal */}
      {showVerifyModal && (
        <div className="modal-overlay">
          <div className="modal-content max-w-md">
            <div className="modal-header">
              <h3 className="modal-title">Төлбөр баталгаажуулах</h3>
              <button
                onClick={() => setShowVerifyModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <FiXCircle size={24} />
              </button>
            </div>
            <div className="modal-body">
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-green-800">
                  Төлбөр баталгаажуулсан тохиолдолд барааны үлдэгдэл автоматаар хасагдах болно.
                </p>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Тэмдэглэл (заавал биш)
                </label>
                <textarea
                  value={verifyNote}
                  onChange={(e) => setVerifyNote(e.target.value)}
                  className="input"
                  rows="3"
                  placeholder="Нэмэлт тэмдэглэл..."
                ></textarea>
              </div>
            </div>
            <div className="modal-footer">
              <button
                onClick={() => setShowVerifyModal(false)}
                className="btn btn-secondary"
              >
                Болих
              </button>
              <button
                onClick={handleVerifyPayment}
                disabled={actionLoading}
                className="btn btn-success"
              >
                {actionLoading ? (
                  <>
                    <div className="spinner mr-2"></div>
                    Баталгаажуулж байна...
                  </>
                ) : (
                  <>
                    <FiCheckCircle className="mr-2" />
                    Баталгаажуулах
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="modal-overlay">
          <div className="modal-content max-w-md">
            <div className="modal-header">
              <h3 className="modal-title">Төлбөр татгалзах</h3>
              <button
                onClick={() => setShowRejectModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <FiXCircle size={24} />
              </button>
            </div>
            <div className="modal-body">
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                <p className="text-sm text-red-800">
                  Татгалзах шалтгааныг тодорхой бичнэ үү.
                </p>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Шалтгаан <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={rejectNote}
                  onChange={(e) => setRejectNote(e.target.value)}
                  className="input"
                  rows="3"
                  placeholder="Яагаад татгалзаж байгаагаа тайлбарлана уу..."
                  required
                ></textarea>
              </div>
            </div>
            <div className="modal-footer">
              <button
                onClick={() => setShowRejectModal(false)}
                className="btn btn-secondary"
              >
                Болих
              </button>
              <button
                onClick={handleRejectPayment}
                disabled={actionLoading}
                className="btn btn-danger"
              >
                {actionLoading ? (
                  <>
                    <div className="spinner mr-2"></div>
                    Татгалзаж байна...
                  </>
                ) : (
                  <>
                    <FiXCircle className="mr-2" />
                    Татгалзах
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Status Update Modal */}
      {showStatusModal && (
        <div className="modal-overlay">
          <div className="modal-content max-w-md">
            <div className="modal-header">
              <h3 className="modal-title">Статус өөрчлөх</h3>
              <button
                onClick={() => setShowStatusModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <FiXCircle size={24} />
              </button>
            </div>
            <div className="modal-body">
              <div className="mb-4">
                <p className="text-sm text-gray-600 mb-2">Одоогийн статус:</p>
                {getStatusBadge(order.status)}
              </div>
              <div className="mb-4">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Шинэ статус <span className="text-red-500">*</span>
                </label>
                <select
                  value={newStatus}
                  onChange={(e) => setNewStatus(e.target.value)}
                  className="input"
                  required
                >
                  <option value="">Сонгох...</option>
                  <option value="pending">Хүлээгдэж буй</option>
                  <option value="accepted">Баталгаажсан</option>
                  <option value="prepared">Бэлтгэгдсэн</option>
                  <option value="dispatched">Хүргэлтэнд гарсан</option>
                  <option value="delivered">Хүргэгдсэн</option>
                  <option value="cancelled">Цуцлагдсан</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Тэмдэглэл (заавал биш)
                </label>
                <textarea
                  value={statusNote}
                  onChange={(e) => setStatusNote(e.target.value)}
                  className="input"
                  rows="3"
                  placeholder="Нэмэлт тэмдэглэл..."
                ></textarea>
              </div>
            </div>
            <div className="modal-footer">
              <button
                onClick={() => setShowStatusModal(false)}
                className="btn btn-secondary"
              >
                Болих
              </button>
              <button
                onClick={handleUpdateStatus}
                disabled={actionLoading || !newStatus}
                className="btn btn-primary"
              >
                {actionLoading ? (
                  <>
                    <div className="spinner mr-2"></div>
                    Шинэчилж байна...
                  </>
                ) : (
                  <>
                    <FiEdit className="mr-2" />
                    Шинэчлэх
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ✅ ШИНЭ - Credit Payment Modal */}
      {showCreditPaymentModal && (
        <div className="modal-overlay">
          <div className="modal-content max-w-md">
            <div className="modal-header">
              <h3 className="modal-title">Зээлийн төлбөр бүртгэх</h3>
              <button
                onClick={() => setShowCreditPaymentModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <FiXCircle size={24} />
              </button>
            </div>
            <div className="modal-body">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <p className="text-gray-600">Нийт зээл:</p>
                    <p className="font-bold text-gray-900">₮{order.total_amount.toLocaleString()}</p>
                  </div>
                  <div>
                    <p className="text-gray-600">Үлдэгдэл:</p>
                    <p className="font-bold text-red-600">₮{remainingAmount.toLocaleString()}</p>
                  </div>
                </div>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Төлбөрийн дүн <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  value={creditPaymentAmount}
                  onChange={(e) => setCreditPaymentAmount(e.target.value)}
                  className="input"
                  placeholder="0"
                  min="0"
                  max={remainingAmount}
                  step="0.01"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">
                  Хамгийн их: ₮{remainingAmount.toLocaleString()}
                </p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Тэмдэглэл (заавал биш)
                </label>
                <textarea
                  value={creditPaymentNote}
                  onChange={(e) => setCreditPaymentNote(e.target.value)}
                  className="input"
                  rows="3"
                  placeholder="Төлбөрийн тэмдэглэл..."
                ></textarea>
              </div>
            </div>
            <div className="modal-footer">
              <button
                onClick={() => setShowCreditPaymentModal(false)}
                className="btn btn-secondary"
              >
                Болих
              </button>
              <button
                onClick={handleRecordCreditPayment}
                disabled={actionLoading || !creditPaymentAmount}
                className="btn btn-success"
              >
                {actionLoading ? (
                  <>
                    <div className="spinner mr-2"></div>
                    Бүртгэж байна...
                  </>
                ) : (
                  <>
                    <FiDollarSign className="mr-2" />
                    Төлбөр бүртгэх
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderDetail;